import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import profileImageCache from '../../services/profileImageCache';
import userService from '../../services/userService';
import { typography } from '../../styles/typography';
import { generateShiftTimestamps, getShiftDuration, parseTimeString } from '../../utils/timeUtils';
import { Card, IconButton, IconTextButton, PrimaryButton, SearchBar } from '../ui';
import { employeeApi } from '../ShiftManagement/employeeApi';
import { getSitesForCurrentUser } from '../SiteManagement/siteApi';
import CleaningSelectArea from './CleaningSelectArea';
import OvernightShiftNotice, { ShiftTimeValidationNotice } from './OvernightShiftNotice';
import {
  buildCleaningShiftAreaSummary,
  createEmptyCleaningAreaSelection,
} from './cleaningAreaData';
import {
  fetchAllScheduleUsersForSite,
  fetchCleaningSiteCatalog,
  resolveCommunitySiteId,
} from './cleaningScheduleApi';
import {
  buildCleaningScheduleBulkPayload,
  CLEANING_SCHEDULE_BULK_ENDPOINT,
  createDefaultCleaningDatePickerState,
} from './cleaningSchedulePayload';
import { getScheduleModeConfig } from './scheduleModeConfig';

const modeConfig = getScheduleModeConfig('cleaning');
const labels = modeConfig.labels;
const { getRoster, sendSchedule, deleteSchedule } = modeConfig;

const ScheduleACleaning = () => {
  useScreenLogging(labels.logScreenName, {
    component: labels.logScreenName,
  });
  const location = useLocation();
  const [shiftType, setShiftType] = useState('morning');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedOfficers, setSelectedOfficers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [allSites, setAllSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAllSitesDropdown, setShowAllSitesDropdown] = useState(false);
  
  // Shift management state
  const [shifts, setShifts] = useState([]);
  const [selectedShiftIndex, setSelectedShiftIndex] = useState(0);
  const [isLoadingShifts, setIsLoadingShifts] = useState(false);
  const [shiftsError, setShiftsError] = useState(null);
  
  // Flag to track when we're switching shifts vs when user is changing time
  const [isSwitchingShift, setIsSwitchingShift] = useState(false);
  
  // Schedule shift state
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const [scheduleSuccess, setScheduleSuccess] = useState(false);

  const [siteCatalog, setSiteCatalog] = useState(null);
  const [isLoadingSiteCatalog, setIsLoadingSiteCatalog] = useState(false);
  const [siteCatalogError, setSiteCatalogError] = useState(null);
  const [scheduleUsersById, setScheduleUsersById] = useState({});

  // Get overlay functions and data from context
  const { openOverlay, datePickerData, startTimeData, endTimeData, assignedEmployees, setAssignedEmployees, assignedEmployeesToZones, setAssignedEmployeesToZones, setStartTimeData, setEndTimeData, setDatePickerData, overnightShiftInfo } = useOverlay();

  // Default to today for "Pick date or select multiple dates" (unless URL supplies a date)
  useEffect(() => {
    const dateFromUrl = new URLSearchParams(location.search).get('date');
    if (dateFromUrl) return;

    setDatePickerData(prev => {
      if (prev.variant === 'cleaning' && prev.selectedDates?.length > 0) {
        return prev;
      }
      return createDefaultCleaningDatePickerState({
        assignedDates: prev.assignedDates || [],
      });
    });
  }, [location.search, setDatePickerData]);

  const getCleaningAreaEmployeeCount = cleaningArea => {
    const byCategory = cleaningArea?.selectedEmployeeIdsByCategory || {};
    return Object.values(byCategory).reduce(
      (sum, ids) => sum + (Array.isArray(ids) ? ids.length : 0),
      0
    );
  };

  const currentShiftAreaSummary = useMemo(() => {
    const cleaningArea = shifts[selectedShiftIndex]?.cleaningArea;
    if (!cleaningArea) return null;
    return buildCleaningShiftAreaSummary(cleaningArea, siteCatalog, scheduleUsersById);
  }, [shifts, selectedShiftIndex, siteCatalog, scheduleUsersById]);

  const hasStaffAssigned = () => {
    const cleaningArea = shifts[selectedShiftIndex]?.cleaningArea;
    if (getCleaningAreaEmployeeCount(cleaningArea) > 0) return true;
    return assignedEmployees && assignedEmployees.length > 0;
  };

  // Clear schedule status when form changes
  useEffect(() => {
    setScheduleError(null);
    setScheduleSuccess(false);
  }, [selectedSite, assignedEmployees, assignedEmployeesToZones, shifts, selectedShiftIndex]);

  // Sync context with current shift data when shifts change
  useEffect(() => {
    if (shifts.length > 0 && selectedShiftIndex < shifts.length) {
      const currentShift = shifts[selectedShiftIndex];
      
      // Update supervisors in context
      if (currentShift.supervisors && currentShift.supervisors.length > 0) {
        setAssignedEmployees(currentShift.supervisors);
      } else if (!assignedEmployees || assignedEmployees.length === 0) {
        setAssignedEmployees([]);
      }
      
      // Update guards in context
      if (currentShift.zoneGuards && Object.keys(currentShift.zoneGuards).length > 0) {
        setAssignedEmployeesToZones(currentShift.zoneGuards);
      } else if (!assignedEmployeesToZones || Object.keys(assignedEmployeesToZones).length === 0) {
        setAssignedEmployeesToZones({});
      }
    }
  }, [shifts, selectedShiftIndex]);

  // Helper function to cache profile images for a user
  const cacheUserProfileImage = async (user) => {
    try {
      const userId = user.user_id || user.employee_id;
      if (!userId) return;
      
      const imagePath = user.image_path || user.image_presigned_url;
      if (!imagePath || imagePath.trim() === '') return;
      
      // Check if already cached
      const cachedUrl = profileImageCache.get(userId);
      if (cachedUrl) {
        return; // Already cached
      }
      
      // If it's already a URL, cache it directly
      if (imagePath.startsWith('http')) {
        profileImageCache.set(userId, imagePath);
        console.log(`💾 Cached direct profile image URL for user ${userId}`);
        return;
      }
      
      // Cache the S3 path first
      profileImageCache.setS3Path(userId, imagePath);
      
      // Try to get presigned URL and cache it (async, don't block)
      userService.getS3PresignedUrl(imagePath)
        .then(presignedUrl => {
          if (presignedUrl) {
            profileImageCache.set(userId, presignedUrl, imagePath);
            console.log(`💾 Cached profile image URL for user ${userId}`);
          }
        })
        .catch(error => {
          console.warn(`⚠️ Failed to cache presigned URL for user ${userId}:`, error);
        });
    } catch (error) {
      console.warn(`⚠️ Failed to cache profile image for user ${user.user_id}:`, error);
    }
  };

  // Function to fetch user details for multiple user IDs using bulk API endpoints
  const fetchUserDetails = async (userIds) => {
    if (!userIds || userIds.length === 0) return {};
    
    try {
      // Fetch all users using the working bulk API endpoints
      const [guardsData, supervisorsData] = await Promise.all([
        employeeApi.fetchAvailableGuards().catch(error => {
          console.warn('Failed to fetch guards:', error);
          return [];
        }),
        employeeApi.fetchAvailableSupervisors().catch(error => {
          console.warn('Failed to fetch supervisors:', error);
          return [];
        })
      ]);
      
      // Combine all users into a single array
      const allUsers = [...guardsData, ...supervisorsData];
      
      // Create a map for quick lookup by user_id
      const userDetailsMap = {};
      allUsers.forEach(user => {
        const userId = user.user_id || user.employee_id;
        if (userId) {
          userDetailsMap[userId] = {
            userId,
            name: user.user_name || user.name || user.employee_name || 'Unknown',
            user_name: user.user_name || user.name || user.employee_name || 'Unknown',
            role_name: user.spec_role || user.role_name || 'Unknown',
            ...user
          };
        }
      });
      
      // For any user IDs not found in the bulk data, create Unknown entries
      userIds.forEach(userId => {
        if (!userDetailsMap[userId]) {
          userDetailsMap[userId] = {
            userId,
            name: 'Unknown',
            user_name: 'Unknown',
            role_name: 'Unknown'
          };
        }
      });
      
      // Cache profile images for all users that have image_path
      // This will pre-populate the cache so ProfileImage components can use cached URLs immediately
      const cacheProfileImages = async () => {
        const usersWithImages = Object.values(userDetailsMap)
          .filter(user => (user.image_path || user.image_presigned_url) && 
                         (user.image_path?.trim() !== '' || user.image_presigned_url?.trim() !== ''));
        
        const cachePromises = usersWithImages.map(user => cacheUserProfileImage(user));
        
        // Wait for all cache operations to complete (but don't block on presigned URL fetching)
        await Promise.allSettled(cachePromises);
        console.log(`📦 Profile image caching initiated for ${cachePromises.length} users`);
      };
      
      // Start caching profile images in the background (don't await to avoid blocking)
      cacheProfileImages().catch(error => {
        console.warn('⚠️ Error during profile image caching:', error);
      });
      
      console.log('🔍 Fetched user details map from bulk API:', userDetailsMap);
      return userDetailsMap;
    } catch (error) {
      console.error('Error fetching user details:', error);
      return {};
    }
  };
  
  // Fetch all sites when component mounts
  useEffect(() => {
    fetchAllSites();
  }, []);

  const selectedSiteId = resolveCommunitySiteId(selectedSite);

  useEffect(() => {
    if (!selectedSiteId) {
      setScheduleUsersById({});
      return;
    }

    let cancelled = false;

    const loadScheduleUsers = async () => {
      try {
        const allUsers = await fetchAllScheduleUsersForSite(selectedSiteId);
        if (cancelled) return;

        const byId = {};
        allUsers.forEach(emp => {
          const id = emp.user_id || emp.employee_id;
          if (id) {
            byId[id] = emp;
          }
        });
        setScheduleUsersById(byId);
        console.log('[ScheduleACleaning] Loaded schedule users', {
          siteId: selectedSiteId,
          count: Object.keys(byId).length,
        });
      } catch (error) {
        console.error('[ScheduleACleaning] Failed to load schedule users:', error);
        if (!cancelled) setScheduleUsersById({});
      }
    };

    loadScheduleUsers();
    return () => {
      cancelled = true;
    };
  }, [selectedSiteId]);

  useEffect(() => {
    if (!selectedSiteId) {
      setSiteCatalog(null);
      setSiteCatalogError(null);
      return;
    }

    let cancelled = false;

    const loadCatalog = async () => {
      setIsLoadingSiteCatalog(true);
      setSiteCatalogError(null);
      try {
        const catalog = await fetchCleaningSiteCatalog(selectedSiteId);
        if (!cancelled) {
          setSiteCatalog(catalog);
          console.log('[ScheduleACleaning] siteCatalog state set', {
            siteId: selectedSiteId,
            siteName: selectedSite?.site_name,
            blocks: catalog.blocks,
            blockCount: catalog.blocks?.length ?? 0,
          });
          if (catalog.templatesError) {
            console.warn(
              '[ScheduleACleaning] Task templates unavailable:',
              catalog.templatesError
            );
          }
        }
      } catch (error) {
        console.error('Failed to load site catalog for cleaning:', error);
        if (!cancelled) {
          setSiteCatalog(null);
          setSiteCatalogError(error.message || 'Failed to load site locations');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingSiteCatalog(false);
        }
      }
    };

    loadCatalog();
    return () => {
      cancelled = true;
    };
  }, [selectedSiteId]);

  // Read URL parameters and populate form when component mounts or URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const siteId = params.get('siteId');
    const siteName = params.get('siteName');
    const date = params.get('date');
    const shiftIndex = params.get('shiftIndex');
    const shiftStartTime = params.get('shiftStartTime');
    const shiftEndTime = params.get('shiftEndTime');
    
    // If URL parameters are present, populate the form
    if (siteId && allSites.length > 0) {
      // Find the site by ID
      const site = allSites.find(s => s.site_id === siteId || s.id === siteId);
      if (site) {
        setSelectedSite(site);
        setSearchTerm(siteName || site.site_name || '');
      }
    }
    
    // Set the date if provided in URL parameters
    if (date) {
      const dateObj = new Date(date);
      setDatePickerData({
        mode: 'single',
        startDate: null,
        endDate: null,
        singleDate: dateObj
      });
      setSelectedDate(date);
      console.log('Set date from URL parameter:', date);
    }
    
    // If shift information is provided, we need to wait for shifts to load
    // and then select the appropriate shift
    if (shiftIndex || shiftStartTime || shiftEndTime) {
      console.log('URL parameters provide shift info:', { shiftIndex, shiftStartTime, shiftEndTime });
      // Note: We'll handle shift selection after shifts are loaded
    }
  }, [location.search, allSites, setDatePickerData]);

  // Fetch shifts when site and date are selected
  useEffect(() => {
    if (
      selectedSite &&
      datePickerData.mode === 'multiple' &&
      datePickerData.selectedDates?.length > 0
    ) {
      fetchShifts();
    } else if (selectedSite && datePickerData.mode === 'single' && datePickerData.singleDate) {
      fetchShifts();
    } else if (selectedSite && datePickerData.mode === 'range' && datePickerData.startDate) {
      fetchShifts();
    }
  }, [selectedSite, datePickerData, siteCatalog]);

  // Handle shift selection from URL parameters after shifts are loaded
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shiftIndex = params.get('shiftIndex');
    const shiftId = params.get('shiftId');
    
    // If URL parameters specify a shift, select it after shifts are loaded
    if (shiftIndex && shifts.length > 0) {
      const shiftIndexNum = parseInt(shiftIndex, 10);
      // URL shiftIndex is UI label N ("Shift - N"); API index is 0-based (N - 1)
      const matchingShiftIndex = shifts.findIndex(
        shift =>
          shift.shift_index === shiftIndexNum ||
          shift.shift_index === shiftIndexNum.toString() ||
          shift.schedule_index === shiftIndexNum - 1
      );
      
      if (matchingShiftIndex !== -1) {
        console.log(`Selecting shift with index ${shiftIndex} at position ${matchingShiftIndex}`);
        setSelectedShiftIndex(matchingShiftIndex);
      }
    } else if (shiftId && shifts.length > 0) {
      // Find shift by ss_id if provided
      const matchingShiftIndex = shifts.findIndex(shift => shift.ss_id === shiftId);
      
      if (matchingShiftIndex !== -1) {
        console.log(`Selecting shift with ss_id ${shiftId} at position ${matchingShiftIndex}`);
        setSelectedShiftIndex(matchingShiftIndex);
      }
    }
  }, [shifts, location.search]);

  // Sites the logged-in user may schedule (authorised_ids) — not /site/getall
  const fetchAllSites = async () => {
    try {
      setIsLoading(true);
      const sitesData = await getSitesForCurrentUser();
      console.log('Fetched scheduler sites:', sitesData);

      const sitesList = Array.isArray(sitesData?.sites)
        ? sitesData.sites
        : Array.isArray(sitesData?.data)
          ? sitesData.data
          : Array.isArray(sitesData)
            ? sitesData
            : [];
      const sortedSites = [...sitesList].sort((a, b) => {
        const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
        const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setAllSites(sortedSites);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
      // Fallback to empty array if API fails
      setAllSites([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch shifts from API - Updated to match Flutter structure
  const fetchShifts = async () => {
    if (!selectedSite) return;
    
    try {
      setIsLoadingShifts(true);
      setShiftsError(null);
      
      // Determine the date to use
      let targetDate;
      if (datePickerData.mode === 'multiple' && datePickerData.selectedDates?.length > 0) {
        targetDate = datePickerData.selectedDates[0];
      } else if (datePickerData.mode === 'single') {
        targetDate = datePickerData.singleDate;
      } else if (datePickerData.mode === 'range') {
        targetDate = datePickerData.startDate;
      } else {
        return;
      }
      
      // Format date as YYYY-MM-DD
      const formattedDate = targetDate.toISOString().split('T')[0];
      
      const shiftsData = await getRoster(
        selectedSiteId,
        formattedDate,
        siteCatalog || undefined
      );

      loggingService.logInfo(
        labels.logViewEvent,
        `User viewed cleaning schedules for site ${selectedSiteId} on date ${formattedDate}`,
        {
          endpoint: '/community/schedule',
          method: 'GET',
          siteId: selectedSiteId,
          date: formattedDate,
          shiftCount: Array.isArray(shiftsData) ? shiftsData.length : 0,
        }
      ).catch(logError => {
        console.warn('[ScheduleACleaning] Failed to log event:', logError);
      });

      const allUserIds = new Set();
      shiftsData.forEach(shift => {
        const byCategory = shift.cleaningArea?.selectedEmployeeIdsByCategory || {};
        Object.values(byCategory).forEach(ids => {
          if (Array.isArray(ids)) {
            ids.forEach(id => id && allUserIds.add(id));
          }
        });
      });

      await fetchUserDetails(Array.from(allUserIds));

      const transformedShifts = shiftsData.map((shift, index) => ({
        ...shift,
        shift_index: shift.shift_index ?? index + 1,
        schedule_index: shift.schedule_index ?? index,
        cleaningArea: shift.cleaningArea || createEmptyCleaningAreaSelection(),
        guards: shift.guards || [],
        supervisors: shift.supervisors || [],
        guardNotes: shift.guardNotes || [],
        supervisorNotes: shift.supervisorNotes || [],
        zoneGuards: shift.zoneGuards || {},
      }));
      
      if (transformedShifts.length > 0) {
        setShifts(transformedShifts);
        setSelectedShiftIndex(0);
      } else {
        // Empty roster: keep any in-progress Shift 1 (Select Area); default seeded in finally
        setSelectedShiftIndex(0);
      }

      // If we have shifts, populate the overlay context with the first shift's employee data
      if (transformedShifts.length > 0) {
        const firstShift = transformedShifts[0];
        
        // Set supervisors in context
        if (firstShift.supervisors && firstShift.supervisors.length > 0) {
          setAssignedEmployees(firstShift.supervisors);
        }
        
        // Set guards in context (grouped by zone)
        if (firstShift.zoneGuards && Object.keys(firstShift.zoneGuards).length > 0) {
          setAssignedEmployeesToZones(firstShift.zoneGuards);
        }
        
        console.log('🔍 Populated context with fetched data:', {
          supervisors: firstShift.supervisors,
          zoneGuards: firstShift.zoneGuards
        });
      }
      
    } catch (error) {
      console.error('Failed to fetch shifts:', error);
      setShiftsError(error.message);
      // Do not clear shifts — allow default Shift 1 + Select Area to remain usable
      setShifts(prev => (prev.length > 0 ? prev : []));

      setAssignedEmployees([]);
      setAssignedEmployeesToZones({});
    } finally {
      setIsLoadingShifts(false);
      setShifts(prev => {
        if (prev.length > 0) return prev;
        const hasDate =
          (datePickerData.mode === 'multiple' &&
            datePickerData.selectedDates?.length > 0) ||
          (datePickerData.mode === 'single' && datePickerData.singleDate) ||
          (datePickerData.mode === 'range' && datePickerData.startDate) ||
          datePickerData.singleDate ||
          datePickerData.startDate;
        if (!selectedSite || !hasDate) return prev;
        return [createDefaultShift()];
      });
      setSelectedShiftIndex(0);
      setStartTimeData({ hours: 9, minutes: 0, period: 'AM' });
      setEndTimeData({ hours: 6, minutes: 0, period: 'PM' });
    }
  };

  
  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Format date for display
  const formatDateDisplay = () => {
    if (datePickerData.mode === 'multiple' && datePickerData.selectedDates?.length > 0) {
      const dates = datePickerData.selectedDates;
      if (dates.length === 1) {
        return formatDate(dates[0]);
      }
      if (dates.length <= 3) {
        return dates.map(d => formatDate(d)).join(', ');
      }
      return `${dates.length} dates selected`;
    }
    if (datePickerData.mode === 'single') {
      return formatDate(datePickerData.singleDate);
    }
    return `${formatDate(datePickerData.startDate)} to ${formatDate(datePickerData.endDate)}`;
  };

  // Format time for display
  const formatTimeDisplay = (time) => {
    const hours = time.hours.toString().padStart(2, '0');
    const minutes = time.minutes.toString().padStart(2, '0');
    return `${hours}:${minutes} ${time.period}`;
  };

  const cleaningShiftValidationData = useMemo(() => {
    const currentShift = shifts[selectedShiftIndex];
    let targetDate = null;
    if (datePickerData.mode === 'single' && datePickerData.singleDate) {
      targetDate = datePickerData.singleDate;
    } else if (datePickerData.mode === 'range' && datePickerData.startDate) {
      targetDate = datePickerData.startDate;
    } else if (
      datePickerData.mode === 'multiple' &&
      datePickerData.selectedDates?.length > 0
    ) {
      targetDate = datePickerData.selectedDates[0];
    }
    return {
      date: targetDate ? targetDate.toISOString().split('T')[0] : null,
      startTime: currentShift?.shift_start_time || formatTimeDisplay(startTimeData),
      endTime: currentShift?.shift_end_time || formatTimeDisplay(endTimeData),
    };
  }, [
    shifts,
    selectedShiftIndex,
    datePickerData,
    startTimeData,
    endTimeData,
  ]);

  // Calculate current shift duration
  const getCurrentShiftDuration = () => {
    if (shifts.length === 0 || selectedShiftIndex >= shifts.length) {
      return 0;
    }
    
    const currentShift = shifts[selectedShiftIndex];
    if (!currentShift.shift_start_time || !currentShift.shift_end_time) {
      return 0;
    }
    
    const startTime = parseTimeString(currentShift.shift_start_time);
    const endTime = parseTimeString(currentShift.shift_end_time);
    
    if (!startTime || !endTime) {
      return 0;
    }
    
    return getShiftDuration(startTime, endTime);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Submitting shift:', { 
      shiftType, 
      selectedDate, 
      selectedOfficers, 
      dateMode: datePickerData.mode,
      singleDate: datePickerData.mode === 'single' ? datePickerData.singleDate : null,
      dateRange: datePickerData.mode === 'range' ? { startDate: datePickerData.startDate, endDate: datePickerData.endDate } : null
    });
    // Add your API call logic here
  };

  // Handle search functionality
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (value.length > 0 && allSites.length > 0) {
      // Filter through real site data
      const filteredSites = allSites.filter(site => 
        site.site_name && site.site_name.toLowerCase().includes(value.toLowerCase())
      );
      setSearchSuggestions(filteredSites.map(site => site.site_name));
    } else if (value.length === 0 && allSites.length > 0) {
      // Show all sites when search is empty
      setSearchSuggestions(allSites.map(site => site.site_name));
    } else {
      setSearchSuggestions([]);
    }
  };

  // Handle search bar focus - show all sites
  const handleSearchFocus = () => {
    if (allSites.length > 0) {
      setSearchSuggestions(allSites.map(site => site.site_name));
      setShowAllSitesDropdown(true);
    }
  };

  // Handle search bar blur
  const handleSearchBlur = () => {
    // Delay hiding to allow clicking on suggestions
    setTimeout(() => {
      setShowAllSitesDropdown(false);
    }, 200);
  };

  // Show all sites when search bar is clicked/focused
  const handleSearchBarClick = () => {
    if (allSites.length > 0) {
      setSearchSuggestions(allSites.map(site => site.site_name));
      setShowAllSitesDropdown(true);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchContainer = document.querySelector('.search-container');
      if (searchContainer && !searchContainer.contains(event.target)) {
        setShowAllSitesDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearchSubmit = (value) => {
    console.log('Search submitted:', value);
    // Find the selected site from allSites
    const site = allSites.find(s => s.site_name === value);
    console.log('Site selection:', { value, site, allSitesCount: allSites.length });
    if (site) {
      setSelectedSite(site);
      console.log('Selected site set:', site);
    } else {
      console.log('Site not found for value:', value);
    }
    setShowAllSitesDropdown(false);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion);
    setSearchSuggestions([]);
    setShowAllSitesDropdown(false);
    // Find and set the selected site with full information
    const site = allSites.find(s => s.site_name === suggestion);
    console.log('Suggestion click:', { suggestion, site, allSitesCount: allSites.length });
    if (site) {
      setSelectedSite(site);
      console.log('Selected site set from suggestion:', site);
    } else {
      console.log('Site not found for suggestion:', suggestion);
    }
  };

  const clearSelectedSite = () => {
    setSelectedSite(null);
    setSearchTerm('');
    setSearchSuggestions([]);
    setShowAllSitesDropdown(false);
    // Clear shifts when site is cleared
    setShifts([]);
    setSelectedShiftIndex(0);
    setShiftsError(null);
  };

  // Persist the employees/guards currently selected in context onto the specified shift
  const persistCurrentAssignmentsToShift = (shiftIndex) => {
    if (shiftIndex == null || shiftIndex < 0 || shiftIndex >= shifts.length) return;

    const cloneEmployees = (employees = []) =>
      employees.map(emp => (typeof emp === 'string' ? emp : { ...emp }));

    const cloneZoneAssignments = (zoneAssignments = {}) => {
      const cloned = {};
      Object.entries(zoneAssignments).forEach(([zoneId, guards]) => {
        cloned[zoneId] = (guards || []).map(guard => (typeof guard === 'string' ? guard : { ...guard }));
      });
      return cloned;
    };

    setShifts(prevShifts =>
      prevShifts.map((shift, index) => {
        if (index !== shiftIndex) return shift;

        const supervisors = cloneEmployees(assignedEmployees || []);
        const zoneGuards = cloneZoneAssignments(assignedEmployeesToZones || {});
        const guards = Object.values(zoneGuards).flat();

        return {
          ...shift,
          supervisors,
          zoneGuards,
          guards
        };
      })
    );
  };

  // Shift management functions - Updated to match Flutter structure
  const handleAddShift = () => {
    // Persist the current shift assignments before resetting for a new shift tab
    persistCurrentAssignmentsToShift(selectedShiftIndex);

    // Validate that the shift is not for a previous date
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    let targetDate;
    if (datePickerData.mode === 'single' && datePickerData.singleDate) {
      targetDate = datePickerData.singleDate;
    } else if (datePickerData.mode === 'range' && datePickerData.startDate) {
      targetDate = datePickerData.startDate;
    } else {
      targetDate = new Date();
    }
    
    if (targetDate) {
      const shiftDate = new Date(targetDate);
      shiftDate.setHours(0, 0, 0, 0); // Reset time to start of day
      if (shiftDate < today) {
        alert('Cannot add shifts for previous dates. Please select today or a future date.');
        return;
      }
    }

    // If no shifts exist, create the first one
    if (shifts.length === 0) {
      // console.log("🔍 No shifts exist, creating first shift");
      const defaultShift = createDefaultShift();
      setShifts([defaultShift]);
      setSelectedShiftIndex(0);
      return;
    }
    
    const newShiftIndex = shifts.length + 1;
    
    // Use default time 9:00 AM to 6:00 PM for new shifts
    const defaultStartTime = { hours: 9, minutes: 0, period: 'AM' };
    const defaultEndTime = { hours: 6, minutes: 0, period: 'PM' };
    
    // Generate timestamps with default time
    const timestamps = generateShiftTimestamps(defaultStartTime, defaultEndTime, targetDate);
    
    const newShift = {
      ss_id: `new_shift_${Date.now()}`, // Temporary ID for new shifts
      site_id: selectedSite?.site_id || '',
      date: targetDate.toISOString().split('T')[0],
      shift_start_time: formatTimeDisplay(defaultStartTime),
      shift_end_time: formatTimeDisplay(defaultEndTime),
      shift_type: timestamps.is_overnight ? 'night' : 'day',
      status: 'draft',
      notes: '',
      is_overtime: timestamps.is_overnight,
      break_duration: 60,
      shift_index: newShiftIndex,
      isNew: true,
      from_timestamp: timestamps.from_timestamp,
      to_timestamp: timestamps.to_timestamp,
      // Add empty arrays for employee assignments
      guards: [],
      supervisors: [],
      guardNotes: [],
      supervisorNotes: [],
      zoneGuards: {},
      cleaningArea: createEmptyCleaningAreaSelection(),
    };
    
    // Clear assigned employees and guards to zones for new shift
    setAssignedEmployees([]);
    setAssignedEmployeesToZones({});
    
    // Update context with default time values
    setStartTimeData(defaultStartTime);
    setEndTimeData(defaultEndTime);
    
    setShifts(prevShifts => [...prevShifts, newShift]);
    setSelectedShiftIndex(shifts.length); // Select the newly added shift
  };

  const handleRemoveShift = async (shiftIndex) => {
    // 1. Minimum Shift Validation
    if (shifts.length <= 1) {
      console.log("❌ Cannot delete - only one shift remaining");
      alert('At least one shift must remain. Cannot delete the last shift.');
      return;
    }

    // 2. Past Date Validation
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    let shiftDate = null;
    if (datePickerData.mode === 'single' && datePickerData.singleDate) {
      shiftDate = new Date(datePickerData.singleDate);
    } else if (datePickerData.mode === 'range' && datePickerData.startDate) {
      shiftDate = new Date(datePickerData.startDate);
    }
    
    if (shiftDate) {
      shiftDate.setHours(0, 0, 0, 0); // Reset time to start of day
      if (shiftDate < today) {
        console.log("❌ Cannot delete - shift is for a past date");
        alert('Cannot modify shifts for past dates.');
        return;
      }
    }

    // 3. Show warning modal using overlay system with actual deletion function
    openOverlay('deleteShiftWarning', { 
      shiftIndex,
      onConfirm: async (indexToDelete) => {
        try {
          // Get the shift's ss_id if it exists
          const shift = shifts[indexToDelete];
          const ssId = shift?.ss_id;

          if (ssId && !ssId.startsWith('default_shift_') && !ssId.startsWith('new_shift_')) {
            let formattedDate = null;
            if (datePickerData.mode === 'multiple' && datePickerData.selectedDates?.length > 0) {
              formattedDate = datePickerData.selectedDates[0].toISOString().split('T')[0];
            } else if (datePickerData.mode === 'single' && datePickerData.singleDate) {
              formattedDate = datePickerData.singleDate.toISOString().split('T')[0];
            } else if (datePickerData.mode === 'range' && datePickerData.startDate) {
              formattedDate = datePickerData.startDate.toISOString().split('T')[0];
            }

            const scheduleIndex =
              typeof shift?.schedule_index === 'number'
                ? shift.schedule_index
                : Math.max(0, (shift?.shift_index || indexToDelete + 1) - 1);

            console.log('Deleting community cleaning schedule...', {
              ssId,
              siteId: selectedSiteId,
              date: formattedDate,
              index: scheduleIndex,
            });

            await deleteSchedule(ssId, {
              siteId: selectedSiteId,
              date: formattedDate,
              index: scheduleIndex,
            });
            console.log('Community schedule deleted from backend');
            alert('Community shift deleted successfully!');
          } else {
            console.log("ℹ️ No ss_id found - this was a new shift not yet saved to backend");
          }

          // 5. UI State Updates After Successful Deletion
          setShifts(prevShifts => {
            const updatedShifts = prevShifts.filter((_, index) => index !== indexToDelete);
            
            // Adjust selected shift index if needed
            let newSelectedIndex = selectedShiftIndex;
            if (selectedShiftIndex >= updatedShifts.length) {
              newSelectedIndex = Math.max(0, updatedShifts.length - 1);
            }
            setSelectedShiftIndex(newSelectedIndex);
            
            return updatedShifts;
          });

          // Clear zone-guard assignments for the remaining shifts
          setAssignedEmployeesToZones({});
          
          // Update context with the new selected shift's time if there are remaining shifts
          if (shifts.length > 1) {
            const newSelectedShift = shifts[selectedShiftIndex >= shifts.length - 1 ? shifts.length - 2 : selectedShiftIndex];
            if (newSelectedShift?.shift_start_time && newSelectedShift?.shift_end_time) {
              const parseTimeString = (timeString) => {
                const [time, period] = timeString.split(' ');
                const [hours, minutes] = time.split(':');
                return {
                  hours: parseInt(hours),
                  minutes: parseInt(minutes),
                  period: period
                };
              };
              
              const startTime = parseTimeString(newSelectedShift.shift_start_time);
              const endTime = parseTimeString(newSelectedShift.shift_end_time);
              
              setStartTimeData(startTime);
              setEndTimeData(endTime);
            }
          }

          console.log("✅ Shift deletion process completed successfully");
          
        } catch (error) {
          console.error("❌ Error during deletion process:", error);
          alert(`Error deleting shift: ${error.message}`);
        }
      }
    });
  };

  const handleSelectShift = (shiftIndex) => {
    if (shiftIndex === selectedShiftIndex) return;

    // Save the current shift assignments before switching tabs
    persistCurrentAssignmentsToShift(selectedShiftIndex);

    setIsSwitchingShift(true); // Set flag to indicate we're switching shifts
    setSelectedShiftIndex(shiftIndex);
    
    // Update context with the selected shift's employee data
    if (shifts[shiftIndex]) {
      const selectedShift = shifts[shiftIndex];
      
      // Set supervisors in context
      if (selectedShift.supervisors && selectedShift.supervisors.length > 0) {
        setAssignedEmployees(selectedShift.supervisors);
      } else {
        setAssignedEmployees([]);
      }
      
      // Set guards in context (grouped by zone)
      if (selectedShift.zoneGuards && Object.keys(selectedShift.zoneGuards).length > 0) {
        setAssignedEmployeesToZones(selectedShift.zoneGuards);
      } else {
        setAssignedEmployeesToZones({});
      }
      
      // Update context time data with the selected shift's time
      if (selectedShift.shift_start_time && selectedShift.shift_end_time) {
        // Parse the shift times back to time objects
        const parseTimeString = (timeString) => {
          const [time, period] = timeString.split(' ');
          const [hours, minutes] = time.split(':');
          return {
            hours: parseInt(hours),
            minutes: parseInt(minutes),
            period: period
          };
        };
        
        const startTime = parseTimeString(selectedShift.shift_start_time);
        const endTime = parseTimeString(selectedShift.shift_end_time);
        
        setStartTimeData(startTime);
        setEndTimeData(endTime);
        
        console.log('🔍 Updated context time for shift', shiftIndex, ':', {
          startTime,
          endTime
        });
      }
      
      console.log('🔍 Updated context for shift', shiftIndex, ':', {
        supervisors: selectedShift.supervisors,
        zoneGuards: selectedShift.zoneGuards
      });
    } else {
      // If no shift exists at this index, clear the context
      setAssignedEmployees([]);
      setAssignedEmployeesToZones({});
    }
    
    // Reset the flag after a short delay to allow the time update to complete
    setTimeout(() => {
      setIsSwitchingShift(false);
    }, 100);
  };

  // Update shift timestamps when time changes
  const updateShiftTimestamps = (shiftIndex, startTime, endTime) => {
    if (shiftIndex < 0 || shiftIndex >= shifts.length) return;
    
    // Determine target date
    let targetDate;
    if (datePickerData.mode === 'single' && datePickerData.singleDate) {
      targetDate = datePickerData.singleDate;
    } else if (datePickerData.mode === 'range' && datePickerData.startDate) {
      targetDate = datePickerData.startDate;
    } else {
      targetDate = new Date();
    }
    
    // Generate new timestamps
    const timestamps = generateShiftTimestamps(startTime, endTime, targetDate);
    
    // Update the shift
    setShifts(prevShifts => prevShifts.map((shift, index) => {
      if (index === shiftIndex) {
        return {
          ...shift,
          shift_start_time: formatTimeDisplay(startTime),
          shift_end_time: formatTimeDisplay(endTime),
          shift_type: timestamps.is_overnight ? 'night' : 'day',
          is_overtime: timestamps.is_overnight,
          from_timestamp: timestamps.from_timestamp,
          to_timestamp: timestamps.to_timestamp
        };
      }
      return shift;
    }));
  };

  // Create default shift when none exist - Updated to match Flutter structure
  const createDefaultShift = () => {
    // Use default time 9:00 AM to 6:00 PM for new shifts
    const defaultStartTime = { hours: 9, minutes: 0, period: 'AM' };
    const defaultEndTime = { hours: 6, minutes: 0, period: 'PM' };
    
    // Determine target date
    let targetDate;
    if (datePickerData.mode === 'multiple' && datePickerData.selectedDates?.length > 0) {
      targetDate = datePickerData.selectedDates[0];
    } else if (datePickerData.mode === 'single' && datePickerData.singleDate) {
      targetDate = datePickerData.singleDate;
    } else if (datePickerData.mode === 'range' && datePickerData.startDate) {
      targetDate = datePickerData.startDate;
    } else {
      targetDate = new Date();
    }
    
    // Validate that the shift is not for a previous date
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    if (targetDate) {
      const shiftDate = new Date(targetDate);
      shiftDate.setHours(0, 0, 0, 0); // Reset time to start of day
      if (shiftDate < today) {
        // If the selected date is in the past, use today's date instead
        targetDate = new Date();
        console.warn('Selected date is in the past, using today\'s date instead');
      }
    }
    
    // Generate timestamps with default time
    const timestamps = generateShiftTimestamps(defaultStartTime, defaultEndTime, targetDate);
    
    const defaultShift = {
      ss_id: `default_shift_${Date.now()}`,
      site_id: selectedSite?.site_id || '',
      date: targetDate.toISOString().split('T')[0],
      shift_start_time: formatTimeDisplay(defaultStartTime),
      shift_end_time: formatTimeDisplay(defaultEndTime),
      shift_type: timestamps.is_overnight ? 'night' : 'day',
      status: 'draft',
      notes: '',
      is_overtime: timestamps.is_overnight,
      break_duration: 60,
      shift_index: 1,
      isNew: true,
      from_timestamp: timestamps.from_timestamp,
      to_timestamp: timestamps.to_timestamp,
      // Add empty arrays for employee assignments
      guards: [],
      supervisors: [],
      guardNotes: [],
      supervisorNotes: [],
      zoneGuards: {},
      cleaningArea: createEmptyCleaningAreaSelection(),
    };
    
    return defaultShift;
  };

  const hasCleaningDatesSelected = () => {
    if (datePickerData.mode === 'multiple') {
      return (datePickerData.selectedDates?.length ?? 0) > 0;
    }
    if (datePickerData.mode === 'single') {
      return !!datePickerData.singleDate;
    }
    if (datePickerData.mode === 'range') {
      return !!datePickerData.startDate;
    }
    return !!(datePickerData.startDate || datePickerData.singleDate);
  };

  const handleCleaningAreaChange = areaSelection => {
    setShifts(prevShifts => {
      if (prevShifts.length === 0) {
        return [{ ...createDefaultShift(), cleaningArea: areaSelection }];
      }
      return prevShifts.map((shift, index) =>
        index === selectedShiftIndex
          ? { ...shift, cleaningArea: areaSelection }
          : shift
      );
    });
    setSelectedShiftIndex(0);
  };

  // Effect to create default shift when none exist
  useEffect(() => {
    // console.log("🔍 Default shift effect triggered:", {
    //   shiftsLength: shifts.length,
    //   selectedSite: !!selectedSite,
    //   selectedSiteName: selectedSite?.site_name,
    //   datePickerData: datePickerData,
    //   hasValidDate: !!(datePickerData.startDate || datePickerData.singleDate),
    //   shiftsError: shiftsError,
    //   isLoadingShifts: isLoadingShifts
    // });
    
    // Only create default shift if:
    // 1. No shifts exist
    // 2. Site is selected
    // 3. Date is selected
    // 4. Not currently loading shifts
    // 5. Date is not in the past
    // Note: We create default shift even if there's an API error (shiftsError)
    if (shifts.length === 0 && selectedSite && hasCleaningDatesSelected() && !isLoadingShifts) {
      
      // Check if the selected date is not in the past
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      let selectedDate = null;
      if (datePickerData.mode === 'multiple' && datePickerData.selectedDates?.length > 0) {
        selectedDate = new Date(datePickerData.selectedDates[0]);
      } else if (datePickerData.mode === 'single' && datePickerData.singleDate) {
        selectedDate = new Date(datePickerData.singleDate);
      } else if (datePickerData.mode === 'range' && datePickerData.startDate) {
        selectedDate = new Date(datePickerData.startDate);
      }
      
      if (selectedDate) {
        selectedDate.setHours(0, 0, 0, 0);
        if (selectedDate < today) {
          console.log("🔍 Selected date is in the past, not creating default shift");
          return;
        }
      }
      
      console.log("🔍 Creating default shift - Shift 1");
      const defaultShift = createDefaultShift();
      console.log("🔍 Default shift created:", defaultShift);
      // Calculate and log the shift duration
      const startTime = parseTimeString(defaultShift.shift_start_time);
      const endTime = parseTimeString(defaultShift.shift_end_time);
      const duration = getShiftDuration(startTime, endTime);
      
      console.log("🔍 Shift duration calculation:", {
        startTime: defaultShift.shift_start_time,
        endTime: defaultShift.shift_end_time,
        parsedStartTime: startTime,
        parsedEndTime: endTime,
        calculatedDuration: duration,
        from_timestamp: defaultShift.from_timestamp,
        to_timestamp: defaultShift.to_timestamp
      });
      setShifts([defaultShift]);
      setSelectedShiftIndex(0);
      
      // Clear assigned employees and guards to zones for new default shift
      setAssignedEmployees([]);
      setAssignedEmployeesToZones({});
      
      // Update context with default time values
      setStartTimeData({ hours: 9, minutes: 0, period: 'AM' });
      setEndTimeData({ hours: 6, minutes: 0, period: 'PM' });
    }
  }, [selectedSite, datePickerData, isLoadingShifts]);

  // Effect to update timestamps when time data changes (only when user changes time, not when switching shifts)
  useEffect(() => {
    if (shifts.length > 0 && selectedShiftIndex >= 0 && selectedShiftIndex < shifts.length && !isSwitchingShift) {
      const currentShift = shifts[selectedShiftIndex];
      
      // Check if this is a new shift (not yet saved)
      const isNewShift = currentShift.isNew || 
                        (currentShift.ss_id && (
                          currentShift.ss_id.startsWith('default_shift_') || 
                          currentShift.ss_id.startsWith('new_shift_')
                        ));
      
      // If it's a new shift and has employees assigned, clear them when time changes
      if (isNewShift) {
        const hasSupervisors = assignedEmployees && assignedEmployees.length > 0;
        const hasGuards = assignedEmployeesToZones && Object.keys(assignedEmployeesToZones).length > 0 && 
                         Object.values(assignedEmployeesToZones).some(guards => guards && guards.length > 0);
        
        if (hasStaffAssigned()) {
          console.log('🔍 Clearing employees for new shift due to time change');
          setAssignedEmployees([]);
        }
      }
      
      updateShiftTimestamps(selectedShiftIndex, startTimeData, endTimeData);
    }
  }, [startTimeData, endTimeData, selectedShiftIndex, isSwitchingShift]);

  // Check if current shift is complete and saved (same logic as Flutter)
  const isCurrentShiftComplete = () => {
    // If no shifts exist, allow creating new shifts
    if (shifts.length === 0) {
      console.log("🔍 No shifts exist - allowing shift creation");
      return true; // Allow creating shifts when none exist
    }

    // If selectedShiftIndex is invalid, disable Add Shift
    if (selectedShiftIndex >= shifts.length) {
      console.log("🔍 Invalid shift index - disabling Add Shift button");
      return false;
    }

    const currentShift = shifts[selectedShiftIndex];

    // Check if current shift has required fields filled
    const hasTimeSet = (currentShift.shift_start_time && currentShift.shift_start_time.trim() !== '') &&
                      (currentShift.shift_end_time && currentShift.shift_end_time.trim() !== '');
    
    const hasBeenSaved = currentShift.ss_id && 
                        currentShift.ss_id.toString().trim() !== '' && 
                        !currentShift.ss_id.startsWith('default_shift_') &&
                        !currentShift.ss_id.startsWith('new_shift_');
    const isNotNew = !currentShift.isNew; // Check if shift is not marked as new

    const isComplete = hasTimeSet &&
                      hasStaffAssigned() &&
                      (hasBeenSaved || isNotNew);

    console.log("🔍 Shift completion check for shift", currentShift.shift_index || 'unknown', ":");
    console.log("🔍 Has time set:", hasTimeSet, `(${currentShift.shift_start_time} - ${currentShift.shift_end_time})`);
    console.log("🔍 Has staff assigned:", hasStaffAssigned(), `(${assignedEmployees?.length || 0})`);
    console.log("🔍 Has been saved:", hasBeenSaved, `(ss_id: '${currentShift.ss_id}')`);
    console.log("🔍 Is not new:", isNotNew, `(isNew: ${currentShift.isNew})`);
    console.log("🔍 Is complete:", isComplete);

    return isComplete;
  };

  // Zone assignment handlers - now handled by ZoneAssignmentManager component

  // Schedule shift handler - Updated to match Flutter implementation
  const handleScheduleShift = async () => {
    try {
      setIsScheduling(true);
      setScheduleError(null);
      setScheduleSuccess(false);

      // Comprehensive validation
      if (!selectedSite) {
        throw new Error('Please select a site first');
      }

      if (shifts.length === 0) {
        throw new Error('No shifts to schedule');
      }

      const currentShift = shifts[selectedShiftIndex];
      if (!currentShift) {
        throw new Error('No shift selected');
      }

      // Validate shift times
      if (!currentShift.shift_start_time || !currentShift.shift_end_time) {
        throw new Error('Please set both start and end times for the shift');
      }

      // Validate that at least one employee is assigned
      if (!hasStaffAssigned()) {
        throw new Error(labels.assignBeforeSchedule);
      }

      // Validate that the shift is not for a previous date
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day
      
      if (datePickerData.mode === 'single') {
        if (datePickerData.singleDate) {
          const shiftDate = new Date(datePickerData.singleDate);
          shiftDate.setHours(0, 0, 0, 0);
          if (shiftDate < today) {
            throw new Error('Cannot schedule shifts for previous dates. Please select today or a future date.');
          }
        }
      } else if (datePickerData.mode === 'range') {
        if (datePickerData.startDate && datePickerData.endDate) {
          const startDate = new Date(datePickerData.startDate);
          const endDate = new Date(datePickerData.endDate);
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(0, 0, 0, 0);
          
          // Check if start date is in the past
          if (startDate < today) {
            throw new Error('Cannot schedule shifts for previous dates. Please select today or a future date for the start date.');
          }
          
          // Check if end date is in the past
          if (endDate < today) {
            throw new Error('Cannot schedule shifts for previous dates. Please select today or a future date for the end date.');
          }
          
          // Check if end date is before start date
          if (endDate < startDate) {
            throw new Error('End date cannot be before start date. Please select a valid date range.');
          }
        }
      }

      persistCurrentAssignmentsToShift(selectedShiftIndex);

      const isUpdateOperation =
        currentShift.ss_id &&
        currentShift.ss_id.toString().trim() !== '' &&
        !currentShift.ss_id.startsWith('default_shift_') &&
        !currentShift.ss_id.startsWith('new_shift_');

      const shiftsForPayload = shifts.map((shift, index) =>
        index === selectedShiftIndex
          ? {
              ...shift,
              cleaningArea:
                shift.cleaningArea ||
                currentShift.cleaningArea ||
                createEmptyCleaningAreaSelection(),
            }
          : shift
      );

      const bulkPayload = buildCleaningScheduleBulkPayload({
        siteId: selectedSiteId,
        datePickerData,
        shifts: shiftsForPayload,
        siteCatalog,
      });

      if (!bulkPayload.selected_dates?.length) {
        throw new Error('Please select at least one date');
      }

      console.log('Sending cleaning schedule bulk payload:', bulkPayload);

      const response = await sendSchedule(selectedSiteId, bulkPayload, {
        siteCatalog,
      });
      
      console.log('Shift scheduled successfully:', response);
      console.log('API Response details:', {
        status: response.status || 'unknown',
        message: response.message || 'no message',
        data: response.data || 'no data',
        processedDates: response.processed_dates || 'no processed dates info'
      });
      
      const employeeCount = shiftsForPayload.reduce(
        (sum, shift) => sum + getCleaningAreaEmployeeCount(shift.cleaningArea),
        0
      );
      const dateInfo = datePickerData.mode === 'single' 
        ? datePickerData.singleDate?.toISOString().split('T')[0]
        : `${datePickerData.startDate?.toISOString().split('T')[0]} to ${datePickerData.endDate?.toISOString().split('T')[0]}`;
      
      loggingService.logInfo(
        labels.logScheduleEvent,
        `User ${isUpdateOperation ? 'updated' : 'scheduled'} cleaning for site ${selectedSiteId}`,
        {
          endpoint: CLEANING_SCHEDULE_BULK_ENDPOINT,
          method: 'POST',
          siteId: selectedSiteId,
          date: dateInfo,
          dateMode: datePickerData.mode,
          shiftIndex: selectedShiftIndex + 1,
          shiftStartTime: currentShift.shift_start_time,
          shiftEndTime: currentShift.shift_end_time,
          employeeCount: employeeCount,
          operationType: isUpdateOperation ? 'update' : 'create',
          processedDates: response.processed_dates || bulkPayload.selected_dates.length,
          shiftSlotCount: bulkPayload.shifts.length,
        }
      ).catch(logError => {
        console.warn('[ScheduleShift] Failed to log event:', logError);
      });
      
      setScheduleSuccess(true);
      
      // Show success message with more details
      // Enhanced success message to show date range information
      const dateRangeInfo = datePickerData.mode === 'range' 
        ? `Date Range: ${datePickerData.startDate?.toLocaleDateString()} - ${datePickerData.endDate?.toLocaleDateString()}`
        : `Date: ${datePickerData.singleDate?.toLocaleDateString()}`;
      
      const processedDatesCount =
        response.processed_dates || bulkPayload.selected_dates.length;
      
      const operationType = isUpdateOperation ? 'updated' : 'scheduled';
      
      alert(`${labels.alertSuccessTitle(operationType)}\n\n` +
        `Site: ${selectedSite.site_name}\n` +
        `${dateRangeInfo}\n` +
        `Time: ${currentShift.shift_start_time} - ${currentShift.shift_end_time}\n` +
        `Employees Assigned: ${employeeCount}\n` +
        `${datePickerData.mode === 'range' ? `\n✅ Successfully ${operationType} for ${processedDatesCount} dates in the range.` : ''}`);
      
      const resultsByIndex = new Map(
        (response.results || []).map(result => [result.index, result])
      );

      setShifts(prevShifts =>
        prevShifts.map((shift, index) => {
          const result = resultsByIndex.get(index);
          if (result?.ss_id || index === selectedShiftIndex) {
            return {
              ...shift,
              isNew: false,
              ss_id: result?.ss_id || response.ss_id || shift.ss_id,
              schedule_index:
                typeof result?.index === 'number' ? result.index : shift.schedule_index,
            };
          }
          return shift;
        })
      );
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setScheduleSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error('Error scheduling shift:', error);
      const isForbidden =
        /not authorized|forbidden|403/i.test(error.message || '') ||
        error.status === 403;
      const displayMessage = isForbidden
        ? 'You are not allowed to schedule shifts for this site. Ask an admin to add your user to the site authorised list (site_ids on your user profile), then sign out and sign in again.'
        : error.message;
      setScheduleError(displayMessage);
      alert(`${labels.alertErrorPrefix}: ${displayMessage}`);
    } finally {
      setIsScheduling(false);
    }
  };

  // Zone assignment handlers are now handled by ZoneAssignmentManager component

  return (
    <div className="max-w max-h">
      {labels.previewBanner && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-sm">
          {labels.previewBanner}
        </div>
      )}

      {/* Selected Site Information */}
      <div className="bg-blue-50 rounded-xl p-6">
        
        {/* Instructions Text with Custom h3 Typography */}
      <div className="mb-2">
        <h5 className={typography.h5} style={{color: '#000000'}}>
          {labels.instructionSelectSite}
        </h5>
      </div>
        
        {/* Search Bar Section - Moved inside Selected Site Information */}
        <div className="mb-4">
          <div className="flex items-center gap-4 search-container">
            <div className="flex-1 relative">
              <div onClick={handleSearchBarClick}>
                <SearchBar
                  placeholder={isLoading ? "Loading sites..." : "Search by site name"}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onSearch={handleSearchSubmit}
                  suggestions={searchSuggestions}
                  onSuggestionClick={handleSuggestionClick}
                  className="w-full max-w-2xl ml-0 cursor-pointer" 
                  disabled={isLoading}
                />
              </div>
              {/* Custom dropdown for all sites */}
              {showAllSitesDropdown && allSites.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                  <div className="p-2 border-b border-gray-200 bg-gray-50">
                    <p className="text-sm font-medium text-gray-700">
                      {searchTerm ? `Filtered results (${searchSuggestions.length})` : `All Sites (${allSites.length})`}
                    </p>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {searchSuggestions.map((siteName, index) => (
                      <div
                        key={index}
                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        onClick={() => handleSuggestionClick(siteName)}
                      >
                        <div className="font-medium text-gray-900">{siteName}</div>
                        {allSites.find(s => s.site_name === siteName)?.address && (
                          <div className="text-sm text-gray-500 truncate">
                            {allSites.find(s => s.site_name === siteName)?.address}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Site Details */}
        <div className="flex justify-between items-start mb-4">
          <h5 className={typography.h5} style={{color: '#000000', marginBottom: '0.24rem'}}>
            {selectedSite ? `Selected Site: ${selectedSite.site_name}` : 'Selected Site:'}
          </h5>
        </div>
        {selectedSite ? (
          <div className="space-y-2">
            {/* <p className={typography.p} style={{color: '#000000'}}>
              <span className="font-semibold">Site ID:</span> {selectedSiteId || selectedSite.site_id}
            </p> */}
            <p className={typography.p} style={{color: '#000000'}}>
              <span className="font-semibold">Address:</span> {selectedSite.address}
            </p>
          </div>
        ) : (
          <p className={typography.p} style={{color: '#000000'}}>
            Please search and select a site above
          </p>
        )}
      </div>

      {/* Pick date or date range */}
      <div className="mt-8 bg-blue-50 rounded-xl p-6">
        <h5 className={typography.h5} style={{color: '#000000', marginBottom: '0.24rem'}}>
          {labels.pickDateTitle}
        </h5>
        {/* Date Range Button */}
        <div className="mt-4">
          <Card.Light className="w-full md:w-96 p-3">
            <button 
              className={`w-full inline-flex justify-between items-center transition-opacity duration-200 ${
                selectedSite 
                  ? 'hover:opacity-80 cursor-pointer' 
                  : 'opacity-50 cursor-not-allowed'
              }`}
              onClick={() => {
                if (selectedSite) {
                  openOverlay('datePicker', {
                    variant: 'cleaning',
                    mode: 'multiple',
                    selectedDates: datePickerData.selectedDates || [],
                    assignedDates: datePickerData.assignedDates || [],
                  });
                }
              }}
              disabled={!selectedSite}
            >
              <div className={`text-right justify-start text-lg font-normal ${typography.p} leading-relaxed ${
                selectedSite ? 'text-black' : 'text-gray-400'
              }`}>
                {selectedSite ? formatDateDisplay() : 'Please select a site first'}
              </div>
              <div className="w-6 h-6 relative overflow-hidden">
                {/* Calendar Icon */}
                <svg 
                  className={`w-5 h-5 absolute left-[2px] top-[2px] ${
                    selectedSite ? 'text-black' : 'text-gray-400'
                  }`}
                  fill="currentColor" 
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                </svg>
              </div>
            </button>
          </Card.Light>
        </div>
        
        {/* Past Date Warning */}
        {(() => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (datePickerData.mode === 'single' && datePickerData.singleDate) {
            const selectedDate = new Date(datePickerData.singleDate);
            selectedDate.setHours(0, 0, 0, 0);
            if (selectedDate < today) {
              return (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-amber-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div className="text-amber-800">
                      <p className="font-medium text-sm">
                        Past Date Selected
                      </p>
                      <p className="text-xs mt-1">
                        {labels.pastDateWarning}
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
          } else if (datePickerData.mode === 'range' && datePickerData.startDate && datePickerData.endDate) {
            const startDate = new Date(datePickerData.startDate);
            const endDate = new Date(datePickerData.endDate);
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);
            
            if (startDate < today || endDate < today) {
              return (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-amber-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div className="text-amber-800">
                      <p className="font-medium text-sm">
                        Past Date Selected
                      </p>
                      <p className="text-xs mt-1">
                        {labels.pastDateRangeWarning}
                      </p>
                    </div>
                  </div>
                </div>
              );
            } else if (endDate < startDate) {
              return (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-red-800">
                      <p className="font-medium text-sm">
                        Invalid Date Range
                      </p>
                      <p className="text-xs mt-1">
                        End date cannot be before start date. Please select a valid date range.
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
          }
          return null;
        })()}
      </div>

      {/* Shifts if selected site || Shift Selection to Assign employees*/}
      {selectedSite &&
        (datePickerData.mode === 'single' ||
          datePickerData.mode === 'range' ||
          (datePickerData.mode === 'multiple' && datePickerData.selectedDates?.length > 0)) && (
        <div className="self-stretch inline-flex flex-col justify-start items-start gap-6 w-full mt-8 overflow-x-hidden">
          {/* Loading State */}
          {isLoadingShifts && (
            <div className="w-full p-4 bg-blue-50 rounded-xl">
              <p className={typography.p} style={{color: '#000000'}}>
                {labels.loadingSlots(selectedSite.site_name)}
              </p>
            </div>
          )}

          {/* Error State */}
          {shiftsError && (
            <div className="w-full p-4 bg-red-50 rounded-xl">
              <p className={typography.p} style={{color: '#dc2626'}}>
                {shiftsError}
              </p>
            </div>
          )}

          {/* Shift Tabs */}
          {!isLoadingShifts && (
            <div className="self-stretch h-14 inline-flex justify-start items-center gap-8 w-full">
              <div className="flex-1 flex justify-start items-center gap-6">
                {shifts.length > 0 ? (
                  shifts.map((shift, index) => (
                    <div
                      key={shift.ss_id}
                      className={`w-40 p-3 rounded-xl flex justify-between items-center cursor-pointer transition-all duration-200 ${
                        selectedShiftIndex === index
                          ? 'bg-gradient-to-bl from-stone-400 to-lime-600 shadow-[0px_4px_12px_0px_rgba(34,139,34,0.10)] outline outline-2 outline-offset-[-2px] outline-green-200/10'
                          : 'bg-neutral-50/50 hover:bg-neutral-100'
                      }`}
                      onClick={() => handleSelectShift(index)}
                    >
                      <p 
                        className={typography.p} 
                        style={{
                          color: selectedShiftIndex === index ? '#FFFFFF' : '#000000',
                          fontWeight: selectedShiftIndex === index ? 'bold' : 'normal'
                        }}
                      >
                        {labels.slotTab(shift.shift_index)}
                      </p>
                      <IconButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveShift(index);
                        }}
                        icon={
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path 
                              d="M18 6L6 18M6 6L18 18" 
                              stroke={selectedShiftIndex === index ? "white" : "black"} 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                        }
                      />
                    </div>
                  ))
                ) : (
                  <div className="w-40 p-3 bg-gradient-to-bl from-stone-400 to-lime-600 rounded-xl shadow-[0px_4px_12px_0px_rgba(34,139,34,0.10)] outline outline-2 outline-offset-[-2px] outline-green-200/10 flex justify-between items-center">
                    <p className={typography.p} style={{color: '#FFFFFF', fontWeight: 'bold'}}>
                      {labels.defaultSlotTab}
                    </p>
                    <IconButton 
                      onClick={() => handleRemoveShift(0)}
                      icon={
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      }
                    />
                  </div>
                )}
              </div>
              
              {/* Add Shift Button */}
              <IconTextButton 
                icon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4V20M4 12H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                }
                text={labels.addSlot}
                onClick={isCurrentShiftComplete() ? handleAddShift : () => {
                  const currentShift = shifts[selectedShiftIndex];
                  let message = labels.addSlotBlockedIntro;
                  
                  const hasTimeSet = (currentShift?.shift_start_time && currentShift.shift_start_time.trim() !== '') &&
                                   (currentShift?.shift_end_time && currentShift.shift_end_time.trim() !== '');
                  const hasBeenSaved = currentShift?.ss_id && currentShift.ss_id.toString().trim() !== '' && !currentShift.ss_id.startsWith('default_shift_');
                  
                  if (!hasTimeSet) {
                    message += labels.addSlotSetTimes;
                  }
                  if (!hasStaffAssigned()) {
                    message += labels.addSlotAssignStaff;
                  }
                  if (!hasBeenSaved) {
                    message += labels.addSlotSave;
                  }
                  
                  alert(message);
                }}
                disabled={!isCurrentShiftComplete()}
              />
            </div>
          )}

          {/* Displaying the shift details for the selected shift */}
          {!isLoadingShifts && (
            <div className="self-stretch flex flex-col justify-start items-start gap-6">
              <div className="self-stretch md:h-24 relative">
                <h3 className={typography.h3} style={{color: '#000000', marginBottom: '0.75rem'}}>
                  {shifts.length > 0 
                    ? labels.slotTimeTitle(shifts[selectedShiftIndex]?.shift_index || 1)
                    : labels.defaultSlotTimeTitle
                  }
                </h3>
                {/* Time Range */}
                <div className="w-full md:w-96 h-12 md:absolute md:left-0 md:top-[41px] rounded-xl inline-flex justify-between items-center gap-2 md:gap-0 overflow-hidden">
                  <div 
                    className="flex-1 md:w-44 self-stretch p-3 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-zinc-300 flex justify-between items-start overflow-hidden cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => openOverlay('startTimePicker')}
                  >
                    <div className="justify-start text-black text-lg font-normal font-['Open_Sans'] leading-relaxed">
                      {shifts.length > 0 && shifts[selectedShiftIndex]?.shift_start_time 
                        ? shifts[selectedShiftIndex].shift_start_time
                        : formatTimeDisplay(startTimeData)
                      }
                    </div>
                    <div className="w-6 h-6 relative overflow-hidden flex items-center justify-center flex-shrink-0">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 6v6l4 2M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <h3 className={typography.h3} style={{color: '#000000'}}>
                    ~
                  </h3>
                  <div 
                    className="flex-1 md:w-44 self-stretch p-3 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-zinc-300 flex justify-between items-start overflow-hidden cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => openOverlay('endTimePicker')}
                  >
                    <div className="justify-start text-black text-lg font-normal font-['Open_Sans'] leading-relaxed">
                      {shifts.length > 0 && shifts[selectedShiftIndex]?.shift_end_time 
                        ? shifts[selectedShiftIndex].shift_end_time
                        : formatTimeDisplay(endTimeData)
                      }
                    </div>
                    <div className="w-6 h-6 relative overflow-hidden flex items-center justify-center flex-shrink-0">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 6v6l4 2M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <OvernightShiftNotice
                overnightShiftInfo={overnightShiftInfo}
                title={labels.nightShiftTitle}
                datePickerMode={datePickerData.mode}
              />
              <ShiftTimeValidationNotice overnightShiftInfo={overnightShiftInfo} />

              {/* Select Area — cleaning tasks by block, floor, and category */}
              {siteCatalogError && (
                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                  Could not load site blocks and tasks ({siteCatalogError}).
                </div>
              )}

              <CleaningSelectArea
                value={
                  shifts[selectedShiftIndex]?.cleaningArea ||
                  createEmptyCleaningAreaSelection()
                }
                onChange={handleCleaningAreaChange}
                shiftValidationData={cleaningShiftValidationData}
                siteId={selectedSiteId}
                siteCatalog={siteCatalog}
                isLoadingCatalog={isLoadingSiteCatalog}
              />
              
            </div>
          )}

          {/* Schedule Status Messages */}
          {scheduleSuccess && (
            <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
              ✅ {labels.scheduleSuccess}
            </div>
          )}
          
          {scheduleError && (
            <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              ❌ Error: {scheduleError}
            </div>
          )}

          {/* Shift Summary */}
          {shifts.length > 0 && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
              <h4 className={typography.h5} style={{color: '#000000', marginBottom: '0.5rem'}}>
                {labels.summaryTitle}
              </h4>
              <div className="space-y-3 text-sm font-['Open_Sans']">
                <p><strong>Site:</strong> {selectedSite?.site_name}</p>
                <p><strong>Date:</strong> {formatDateDisplay()}</p>
                <p><strong>Time:</strong> {shifts[selectedShiftIndex]?.shift_start_time} - {shifts[selectedShiftIndex]?.shift_end_time}</p>
                <p><strong>Duration:</strong> {getCurrentShiftDuration().toFixed(1)} hours</p>
                <p>
                  <strong>Employees:</strong>{' '}
                  {getCleaningAreaEmployeeCount(shifts[selectedShiftIndex]?.cleaningArea) ||
                    assignedEmployees?.length ||
                    0}
                </p>

                {currentShiftAreaSummary?.blocks?.length > 0 && (
                  <div className="pt-2 border-t border-gray-200">
                    <p className="font-semibold text-black mb-2">Blocks / Streets</p>
                    <ul className="space-y-2 list-none pl-0">
                      {currentShiftAreaSummary.blocks.map(block => (
                        <li key={block.id} className="pl-0">
                          <span className="font-medium text-black">{block.label}</span>
                          {block.blockType && block.blockType !== 'block' && (
                            <span className="text-gray-500 ml-1">({block.blockType})</span>
                          )}
                          {block.floors.length > 0 ? (
                            <ul className="mt-1 ml-4 space-y-1 list-disc text-gray-700">
                              {block.floors.map(floor => (
                                <li key={`${block.id}-${floor.label}`}>
                                  {floor.label}
                                  {floor.securitySelected && floor.tasks.length === 0 && (
                                    <span className="text-gray-500"> — assigned</span>
                                  )}
                                  {floor.tasks.length > 0 && (
                                    <span className="text-gray-600">
                                      {' '}
                                      — {floor.tasks.join(', ')}
                                    </span>
                                  )}
                                  {floor.description && (
                                    <span className="block text-gray-500 italic text-xs mt-0.5">
                                      {floor.description}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-gray-500 ml-1">— selected (no floor detail)</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {currentShiftAreaSummary?.categories?.length > 0 && (
                  <div className="pt-2 border-t border-gray-200">
                    <p className="font-semibold text-black mb-2">Staff by category</p>
                    <ul className="space-y-1 list-none pl-0">
                      {currentShiftAreaSummary.categories.map(cat => (
                        <li key={cat.id} className="mb-1">
                          <span className="font-medium">{cat.tabLabel}</span>
                          <span className="text-gray-600">
                            {' '}
                            ({cat.sectionLabel}) — {cat.employeeCount} staff
                          </span>
                          {cat.employeeNames?.length > 0 && (
                            <p className="text-gray-700 ml-2 mt-0.5">
                              {cat.employeeNames.join(', ')}
                            </p>
                          )}
                          {cat.description && (
                            <p className="text-gray-500 italic text-xs ml-2 mt-0.5">
                              {cat.description}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {currentShiftAreaSummary?.amenities?.length > 0 && (
                  <div className="pt-2 border-t border-gray-200">
                    <p className="font-semibold text-black mb-1">Amenities (site)</p>
                    <p className="text-gray-600">
                      {currentShiftAreaSummary.amenities.map(a => a.label).join(', ')}
                    </p>
                  </div>
                )}

                {!currentShiftAreaSummary && (
                  <p className="text-amber-800 pt-2 border-t border-gray-200">
                    No blocks or streets selected yet.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Primary Button */}
        <div className="mt-6 w-full overflow-x-hidden">
          <PrimaryButton 
            onClick={handleScheduleShift}
            disabled={isScheduling || !selectedSite || shifts.length === 0 || 
              !hasStaffAssigned() ||
              (() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                if (datePickerData.mode === 'single' && datePickerData.singleDate) {
                  const selectedDate = new Date(datePickerData.singleDate);
                  selectedDate.setHours(0, 0, 0, 0);
                  return selectedDate < today;
                } else if (datePickerData.mode === 'range' && datePickerData.startDate && datePickerData.endDate) {
                  const startDate = new Date(datePickerData.startDate);
                  const endDate = new Date(datePickerData.endDate);
                  startDate.setHours(0, 0, 0, 0);
                  endDate.setHours(0, 0, 0, 0);
                  return startDate < today || endDate < today || endDate < startDate;
                }
                return false;
              })()}
            className="w-full max-w-full"
          >
            {isScheduling ? labels.scheduleButtonLoading : labels.scheduleButton}
          </PrimaryButton>
          
          {/* Help text */}
          {(() => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            let isPastDate = false;
            let isInvalidRange = false;
            
            if (datePickerData.mode === 'single' && datePickerData.singleDate) {
              const selectedDate = new Date(datePickerData.singleDate);
              selectedDate.setHours(0, 0, 0, 0);
              isPastDate = selectedDate < today;
            } else if (datePickerData.mode === 'range' && datePickerData.startDate && datePickerData.endDate) {
              const startDate = new Date(datePickerData.startDate);
              const endDate = new Date(datePickerData.endDate);
              startDate.setHours(0, 0, 0, 0);
              endDate.setHours(0, 0, 0, 0);
              isPastDate = startDate < today || endDate < today;
              isInvalidRange = endDate < startDate;
            }
            
            const hasNoEmployees = !hasStaffAssigned();
            
            if (isInvalidRange) {
              return (
                <p className="mt-2 text-sm text-red-600">
                  ❌ End date cannot be before start date. Please select a valid date range.
                </p>
              );
            } else if (isPastDate) {
              return (
                <p className="mt-2 text-sm text-red-600">
                  ❌ {labels.cannotSchedulePast}
                </p>
              );
            } else if (hasNoEmployees) {
              return (
                <p className="mt-2 text-sm text-amber-600">
                  ⚠️ {labels.assignBeforeSchedule}
                </p>
              );
            }
            return null;
          })()}
        </div>
        </div>
      )}
      

      

      {/* DatePicker Component */}
      {/* This component is now managed by OverlayContext */}

      {/* TimePicker Components */}
      {/* These components are now managed by OverlayContext */}
      
    </div>
  );
};

export default ScheduleACleaning;